function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex } from "sprout-ui-primitives";
import { PostPageCommentRepliesDocument, PostPageComment_CommentFragmentDoc } from "sprout-graphql";
import { media, themeGet } from "sprout-ui-theme";
import styled, { css } from "styled-components";
import Avatar from "@sprout/client-components/dist/Avatar";
import { Button } from "sprout-ui-dom/dist/Button";
import useCurrentUser from "@sprout/client-graphql/dist/useCurrentUser";
import usePromiseLazyQuery from "@sprout/hooks/dist/usePromiseLazyQuery";
import update from "immutability-helper";
import { useLocation } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { getIsLargePointerDevice } from "sprout-ui-dom/dist/utils";
import BasePostPageCard from "../PostPageCard";
import { usePostPageEditor } from "../PostPageEditorContext";
import PostPageCommentEditor from "../PostPageCommentEditor";
import PostPageCommentText from "./PostPageCommentText";
import PostPageCommentReplies from "./PostPageCommentReplies";
import PostPageCommentMeta from "./PostPageCommentMeta";
import { PostPageCommentProvider } from "./PostPageCommentContext";
import useCommentFocus from "./useCommentFocus";
const PostPageCommentReplyWrapper = styled(Flex)`
  flex-direction: column;

  ${media.up("small")} {
    flex-direction: row;
  }
`;
const PostPageCard = styled(BasePostPageCard)`
  padding-top: 16px;
  padding-bottom: 16px;

  background: ${themeGet("colors.white.100")};

  ${media.up("medium", "pointer")} {
    padding-top: 24px;
    padding-bottom: 24px;
  }

  ${({ $focused  })=>$focused && css`
      border: 1px solid ${themeGet("colors.blue.500")};
    `}
`;
const PostPageComment = (props)=>{
    const { comment , parentItemId , parentItemNodeId  } = props;
    const { owner , id , replies , nodeId  } = comment;
    const { totalCount , nodes: nodes1 , pageInfo: { endCursor  } ,  } = replies;
    const currentUser = useCurrentUser();
    const { state , dispatch  } = usePostPageEditor();
    //
    const replyToThread = useCallback(()=>{
        dispatch({
            type: "SET_EDITOR",
            payload: {
                editor: "REPLY_COMMENT",
                editorPosition: id,
                variant: getIsLargePointerDevice() ? "desktop" : "mobile"
            }
        });
    }, [
        dispatch,
        id
    ]);
    //
    const client = useApolloClient();
    const pendingRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const fetchAllReplies = usePromiseLazyQuery(PostPageCommentRepliesDocument);
    const loadMore = useCallback((cb)=>{
        if (pendingRef.current) return;
        setLoading(true);
        pendingRef.current = true;
        fetchAllReplies({
            variables: {
                commentId: id,
                endCursor
            }
        }).then(({ data  })=>{
            var ref;
            const moreReplies = (ref = data.commentById) === null || ref === void 0 ? void 0 : ref.replies;
            if (moreReplies) {
                const options = {
                    id: nodeId,
                    fragment: PostPageComment_CommentFragmentDoc,
                    fragmentName: "PostPageComment_Comment"
                };
                const cache = client.readFragment(options);
                if (cache) client.writeFragment(_objectSpread({}, options, {
                    data: update(cache, {
                        replies: {
                            totalCount: {
                                $set: moreReplies.totalCount
                            },
                            pageInfo: {
                                $set: moreReplies.pageInfo
                            },
                            nodes: {
                                $apply (nodes) {
                                    const existingReplies = nodes.map((node)=>node.id);
                                    return [
                                        ...nodes,
                                        ...moreReplies.nodes.filter((node)=>!existingReplies.includes(node.id)), 
                                    ];
                                }
                            }
                        }
                    })
                }));
            }
        }).finally(()=>{
            if (cb) cb();
            setLoading(false);
            pendingRef.current = false;
        });
    }, [
        client,
        endCursor,
        fetchAllReplies,
        id,
        nodeId
    ]);
    //
    const excessReplyCount = totalCount - nodes1.length;
    const handleReplyClick = useCallback(()=>{
        if (excessReplyCount > 0) loadMore(replyToThread);
        else replyToThread();
    }, [
        excessReplyCount,
        loadMore,
        replyToThread
    ]);
    const initialState = useMemo(()=>({
            parentItemId,
            parentItemNodeId,
            commentId: id
        }), [
        id,
        parentItemId,
        parentItemNodeId
    ]);
    //
    const { hash  } = useLocation();
    const commentHash = `#comment-${id}`;
    const { focused , ref: ref1  } = useCommentFocus(commentHash);
    const currentRepliesIds = useMemo(()=>replies.nodes.map((reply)=>reply.id), [
        replies.nodes
    ]);
    useEffect(()=>{
        const match = /^#(comment|reply)-(\d+)(?:-(\d+))?$/.exec(hash);
        const parentId = match && match[3] ? Number(match[3]) : undefined;
        const replyId = match && match[2] ? Number(match[2]) : undefined;
        if (replyId && !currentRepliesIds.includes(replyId) && parentId === id) loadMore();
    }, [
        currentRepliesIds,
        hash,
        id,
        loadMore
    ]);
    if (!owner) return null;
    const openEditCommentEditor = state.editor === "EDIT_COMMENT" && state.editorPosition === id;
    const openReplyCommentEditor = state.editor === "REPLY_COMMENT" && state.editorPosition === id;
    const showReply = openReplyCommentEditor ? state.variant === "mobile" : true;
    return /*#__PURE__*/ _jsx(PostPageCommentProvider, {
        initialState: initialState,
        children: /*#__PURE__*/ _jsx(Box, {
            children: /*#__PURE__*/ _jsxs(PostPageCard, {
                id: commentHash,
                $focused: focused,
                ref: ref1,
                children: [
                    /*#__PURE__*/ _jsx(PostPageCommentEditor, {
                        itemId: parentItemId,
                        cacheKey: `#${commentHash}`,
                        comment: comment,
                        open: openEditCommentEditor ? state.variant : undefined
                    }),
                    !(openEditCommentEditor && state.variant === "desktop") && /*#__PURE__*/ _jsxs(Box, {
                        children: [
                            /*#__PURE__*/ _jsxs(Flex, {
                                alignItems: "center",
                                mb: 2,
                                children: [
                                    /*#__PURE__*/ _jsx(Box, {
                                        flex: "none",
                                        mr: 2,
                                        children: /*#__PURE__*/ _jsx(Avatar, {
                                            navigable: true,
                                            user: owner,
                                            size: 36
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(PostPageCommentMeta, {
                                        comment: comment
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(PostPageCommentText, {
                                comment: comment
                            })
                        ]
                    }),
                    nodes1.length > 0 && /*#__PURE__*/ _jsx(PostPageCommentReplies, {
                        itemId: parentItemId,
                        parentCommentId: id,
                        parentCommentNodeId: nodeId,
                        excessReplyCount: excessReplyCount,
                        replies: nodes1,
                        loadMore: loadMore,
                        loading: loading
                    }),
                    currentUser && showReply && /*#__PURE__*/ _jsx(PostPageCommentReplyWrapper, {
                        mt: 3,
                        children: /*#__PURE__*/ _jsx(Button, {
                            variant: "bright-teal",
                            onClick: handleReplyClick,
                            disabled: loading,
                            children: "Reply to thread"
                        })
                    }),
                    /*#__PURE__*/ _jsx(PostPageCommentEditor, {
                        itemId: parentItemId,
                        parentItem: comment,
                        cacheKey: `#reply-${id}`,
                        desktopMt: 3,
                        open: openReplyCommentEditor ? state.variant : undefined
                    })
                ]
            })
        })
    });
};
export default PostPageComment;
